import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import {
  ArrowLeft,
  FileText,
  Plus,
  Trash2,
  Download,
  Upload,
  Loader2,
  Megaphone,
  Calendar,
  User,
  ChevronDown,
  ChevronUp,
  Building2,
  Briefcase,
  Calculator,
  Heart,
  Scale,
  Shield,
  Zap
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";

const MACRO_AREE = [
  { value: "ORTOPEDIA", label: "Ortopedia", icon: Building2, color: "bg-blue-100 text-blue-600" },
  { value: "FINANZIARIO", label: "Finanziario", icon: Briefcase, color: "bg-green-100 text-green-600" },
  { value: "CAF", label: "CAF", icon: Calculator, color: "bg-purple-100 text-purple-600" },
  { value: "PATRONATO", label: "Patronato", icon: Heart, color: "bg-orange-100 text-orange-600" },
  { value: "LEGALE", label: "Legale", icon: Scale, color: "bg-red-100 text-red-600" },
  { value: "ASSICURAZIONE", label: "Assicurazione", icon: Shield, color: "bg-teal-100 text-teal-600" },
  { value: "LUCE_E_GAS", label: "Luce e Gas", icon: Zap, color: "bg-amber-100 text-amber-600" }
];

const MarketingMaterials = ({ user }) => {
  const navigate = useNavigate();
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newMaterial, setNewMaterial] = useState({ titolo: "", descrizione: "", macro_area: "" });
  const [selectedFile, setSelectedFile] = useState(null);
  const [expandedAreas, setExpandedAreas] = useState({});
  
  const isAdmin = user?.role === "admin";
  const isTitolare = user?.role === "titolare";
  const canUpload = isAdmin || isTitolare;
  
  // Get visible macro areas based on user role
  const visibleAreas = isAdmin 
    ? MACRO_AREE 
    : isTitolare && user?.macro_area
      ? MACRO_AREE.filter(a => a.value === user.macro_area)
      : user?.role === "collaboratore" && Array.isArray(user?.macro_aree_visibili)
        ? MACRO_AREE.filter(a => user.macro_aree_visibili.includes(a.value))
        : MACRO_AREE;
  
  useEffect(() => {
    fetchMaterials();
    // Expand first area by default
    if (visibleAreas.length > 0) {
      setExpandedAreas({ [visibleAreas[0].value]: true });
    }
  }, []);
  
  const fetchMaterials = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/marketing-materials`, { withCredentials: true });
      setMaterials(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento materiali");
    } finally {
      setLoading(false);
    }
  };
  
  const handleUpload = async () => {
    if (!newMaterial.titolo || !newMaterial.macro_area || !selectedFile) {
      toast.error("Inserisci titolo, seleziona macro area e file");
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("titolo", newMaterial.titolo);
      formData.append("macro_area", newMaterial.macro_area);
      if (newMaterial.descrizione) {
        formData.append("descrizione", newMaterial.descrizione);
      }
      formData.append("file", selectedFile);
      
      await axios.post(`${API}/marketing-materials`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast.success("Materiale pubblicitario caricato");
      setDialogOpen(false);
      setNewMaterial({ titolo: "", descrizione: "", macro_area: isTitolare ? user?.macro_area : "" });
      setSelectedFile(null);
      fetchMaterials();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel caricamento");
    } finally {
      setUploading(false);
    }
  };
  
  const handleDownload = async (materialId, filename) => {
    try {
      const response = await axios.get(`${API}/marketing-materials/${materialId}`, { withCredentials: true });
      
      const byteCharacters = atob(response.data.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: response.data.content_type });
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success("Download completato");
    } catch (error) {
      toast.error("Errore nel download");
    }
  };
  
  const handleDelete = async (materialId) => {
    if (!confirm("Eliminare questo materiale?")) return;
    
    try {
      await axios.delete(`${API}/marketing-materials/${materialId}`, { withCredentials: true });
      toast.success("Materiale eliminato");
      fetchMaterials();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    }
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };
  
  const getFileIcon = (filename) => {
    const ext = filename?.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return "📄";
    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return "🖼️";
    if (['doc', 'docx'].includes(ext)) return "📝";
    if (['xls', 'xlsx'].includes(ext)) return "📊";
    return "📎";
  };
  
  const toggleArea = (areaValue) => {
    setExpandedAreas(prev => ({
      ...prev,
      [areaValue]: !prev[areaValue]
    }));
  };
  
  const getMaterialsByArea = (areaValue) => {
    return materials.filter(m => m.macro_area === areaValue);
  };
  
  const openUploadDialog = () => {
    // Pre-select macro area for titolare
    if (isTitolare && user?.macro_area) {
      setNewMaterial({ titolo: "", descrizione: "", macro_area: user.macro_area });
    }
    setDialogOpen(true);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="marketing-materials-page">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/dashboard")}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Materiale Pubblicitario
                </h1>
                <p className="text-xs text-slate-500">
                  Organizzati per macro area • {materials.length} documenti totali
                </p>
              </div>
            </div>
            
            {canUpload && (
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    className="bg-slate-900 hover:bg-slate-800" 
                    data-testid="upload-material-btn"
                    onClick={openUploadDialog}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Nuovo Materiale
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Carica Materiale Pubblicitario</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div>
                      <Label htmlFor="macro_area">Macro Area *</Label>
                      {isTitolare ? (
                        <Input
                          value={MACRO_AREE.find(a => a.value === user?.macro_area)?.label || user?.macro_area}
                          disabled
                          className="mt-1.5 bg-slate-100"
                        />
                      ) : (
                        <Select
                          value={newMaterial.macro_area}
                          onValueChange={(value) => setNewMaterial(prev => ({ ...prev, macro_area: value }))}
                        >
                          <SelectTrigger className="mt-1.5">
                            <SelectValue placeholder="Seleziona macro area" />
                          </SelectTrigger>
                          <SelectContent>
                            {MACRO_AREE.map((area) => (
                              <SelectItem key={area.value} value={area.value}>
                                {area.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    
                    <div>
                      <Label htmlFor="titolo">Titolo *</Label>
                      <Input
                        id="titolo"
                        value={newMaterial.titolo}
                        onChange={(e) => setNewMaterial(prev => ({ ...prev, titolo: e.target.value }))}
                        placeholder="Es: Brochure Ortopedia - Febbraio 2026"
                        className="mt-1.5"
                        data-testid="material-title-input"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="descrizione">Descrizione</Label>
                      <Textarea
                        id="descrizione"
                        value={newMaterial.descrizione}
                        onChange={(e) => setNewMaterial(prev => ({ ...prev, descrizione: e.target.value }))}
                        placeholder="Descrizione del materiale..."
                        className="mt-1.5"
                        data-testid="material-desc-input"
                      />
                    </div>
                    
                    <div>
                      <Label>File *</Label>
                      <div className="mt-1.5 border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-slate-300 transition-colors">
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                          onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                          className="hidden"
                          id="material-file"
                          data-testid="material-file-input"
                        />
                        <label htmlFor="material-file" className="cursor-pointer">
                          <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                          {selectedFile ? (
                            <p className="text-sm text-slate-900 font-medium">{selectedFile.name}</p>
                          ) : (
                            <p className="text-sm text-slate-600">Clicca per selezionare un file</p>
                          )}
                          <p className="text-xs text-slate-400 mt-1">PDF, DOC, XLS, immagini</p>
                        </label>
                      </div>
                    </div>
                    
                    <div className="flex justify-end gap-3 pt-4">
                      <Button variant="outline" onClick={() => setDialogOpen(false)}>
                        Annulla
                      </Button>
                      <Button
                        onClick={handleUpload}
                        disabled={uploading}
                        className="bg-slate-900 hover:bg-slate-800"
                        data-testid="confirm-upload-btn"
                      >
                        {uploading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Caricamento...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            Carica
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="card-hover p-4">
                <Skeleton className="h-12 w-full" />
              </div>
            ))}
          </div>
        ) : materials.length === 0 ? (
          <div className="card-hover p-12 text-center">
            <Megaphone className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 mb-4">
              {canUpload 
                ? "Inizia caricando il primo materiale pubblicitario" 
                : "Nessun materiale disponibile"}
            </p>
            {canUpload && (
              <Button variant="outline" onClick={openUploadDialog}>
                <Plus className="w-4 h-4 mr-2" />
                Carica il primo materiale
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {visibleAreas.map((area) => {
              const areaMaterials = getMaterialsByArea(area.value);
              const AreaIcon = area.icon;
              const isExpanded = expandedAreas[area.value];
              
              return (
                <div key={area.value} className="card-hover overflow-hidden">
                  {/* Area Header */}
                  <button
                    onClick={() => toggleArea(area.value)}
                    className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                    data-testid={`area-toggle-${area.value}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2.5 rounded-xl ${area.color}`}>
                        <AreaIcon className="w-5 h-5" />
                      </div>
                      <div className="text-left">
                        <h3 className="font-semibold text-slate-900">{area.label}</h3>
                        <p className="text-sm text-slate-500">
                          {areaMaterials.length} {areaMaterials.length === 1 ? "materiale" : "materiali"}
                        </p>
                      </div>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    )}
                  </button>
                  
                  {/* Area Content */}
                  {isExpanded && (
                    <div className="border-t border-slate-100">
                      {areaMaterials.length === 0 ? (
                        <div className="p-8 text-center">
                          <p className="text-slate-400 text-sm">
                            Nessun materiale per questa area
                          </p>
                          {canUpload && (isAdmin || (isTitolare && user?.macro_area === area.value)) && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="mt-3"
                              onClick={() => {
                                setNewMaterial({ titolo: "", descrizione: "", macro_area: area.value });
                                setDialogOpen(true);
                              }}
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Aggiungi
                            </Button>
                          )}
                        </div>
                      ) : (
                        <div className="divide-y divide-slate-100">
                          {areaMaterials.map((material) => (
                            <div
                              key={material.material_id}
                              className="p-4 hover:bg-slate-50 transition-colors flex items-center gap-4"
                              data-testid={`material-${material.material_id}`}
                            >
                              <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-pink-100 rounded-lg flex items-center justify-center text-xl flex-shrink-0">
                                {getFileIcon(material.filename)}
                              </div>
                              
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-slate-900 truncate">{material.titolo}</h4>
                                <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-400">
                                  <span className="flex items-center gap-1">
                                    <FileText className="w-3 h-3" />
                                    {material.filename}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    {formatDate(material.created_at)}
                                  </span>
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDownload(material.material_id, material.filename)}
                                  data-testid={`download-${material.material_id}`}
                                >
                                  <Download className="w-4 h-4" />
                                </Button>
                                
                                {canUpload && (isAdmin || (isTitolare && user?.macro_area === material.macro_area)) && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-red-600 hover:bg-red-50"
                                    onClick={() => handleDelete(material.material_id)}
                                    data-testid={`delete-${material.material_id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default MarketingMaterials;
